import { useCallback, useState, MouseEvent } from "react"
import { GetApp } from "../../App"
import { CloseButton } from "../../Components/CloseButton"
import { ConfirmDialog } from "../../Components/ConfirmDialog"
import { ApiClient, CurrentLogApi, IAppResponse, ILogAutoType, ILogMetadata } from "../../Types"

// Wire contract for cancelling a provisional entry, kept in its own module (mirroring
// `UserAdviceViewsData`) so tests can pin the method + path + body at a single seam.
export const ProvisionalCancelData = {
  cancel(api: ApiClient, key: string): Promise<IAppResponse> {
    return new CurrentLogApi(api).current.cancelProvisional({ params: {}, body: { key } }).call();
  },
};

// The stable `provisionalKey` a cancel request should address for this entry, or
// `undefined` when the entry isn't cancellable.
//
// Two kinds of provisional can be cancelled. A course change (#872) is addressed by
// whichever of its two keys the row carries — its `begin` marker and `end` summary are
// two views of a single maneuver, and the engine treats either as "discard this course
// change". The automatic pause (#1218) is addressed by its own key.
//
// The debounced NMEA connection-loss marker (#1204) deliberately has no cancel
// affordance: the loss is a fact about the connection rather than an inference the user
// can overrule.
export function provisionalCancelKey(metadata: ILogMetadata): string | undefined {
  if (metadata.type != ILogAutoType.courseChange && metadata.type != ILogAutoType.speedChange) {
    return undefined;
  }
  return metadata.provisionalKey || undefined;
}

interface ProvisionalCancelProps {
  cancelKey: string;
  // The entry's `LogMetadata.type`, selecting the confirmation copy: discarding a
  // course change and discarding a pause need to say different things.
  kind: string;
}

// The cancel affordance on a live provisional row, gated behind a confirmation since it
// silently discards an entry the ship would otherwise have recorded.
export function ProvisionalCancel({ cancelKey, kind }: ProvisionalCancelProps) {
  const app = GetApp();
  const t = app.getT("LogView");
  const [confirming, setConfirming] = useState(false);

  const open = useCallback((e?: MouseEvent) => {
    // The mobile header toggles row selection on click, so this must not bubble.
    e?.preventDefault();
    e?.stopPropagation();
    setConfirming(true);
  }, []);

  // Left to throw on failure: `ConfirmDialog`'s `awaitConfirm` keeps the dialog open and
  // surfaces `errorMessage`, so a failed cancel doesn't silently look like it worked.
  const confirm = useCallback(async () => {
    await ProvisionalCancelData.cancel(app.apiClient, cancelKey);
    setConfirming(false);
  }, [app, cancelKey]);

  return (
    <>
      <CloseButton className="ProvisionalCancel" onClick={open} />
      {confirming && (
        <ConfirmDialog
          awaitConfirm={true}
          cancelLabel={t("provisional_cancel_keep")}
          confirmLabel={t("provisional_cancel_discard")}
          errorMessage={t("provisional_cancel_error")}
          message={t(`provisional_cancel_message_${kind}`)}
          onCancel={() => setConfirming(false)}
          onConfirm={confirm}
          title={t(`provisional_cancel_title_${kind}`)}
        />
      )}
    </>
  );
}
